/**
 * Toast
 * Accessible toast notification component with warning/success/error variants.
 * Supports auto-dismiss (paused on hover/focus),
 * manual close button and an entrance animation (skipped when prefers-reduced-motion is set).
 */

export const ToastType = {
  WARNING: 'warning',
  SUCCESS: 'success',
  ERROR: 'error',
};

import { getContainer, prefersReducedMotion } from './utils';
import { createIcon } from '../icons';

export class Toast {
  constructor({
    message,
    type = ToastType.WARNING,
    autoDismiss = 6000,
    closeLabel = 'Close notification',
    iconKey,
    actionLabel,
    onAction,
    pauseOnInteraction = true,
    onClose,
    container,
  } = {}) {
    this._message = message;
    this._type = type;
    this._autoDismiss = autoDismiss;
    this._closeLabel = closeLabel;
    this._iconKey = iconKey;
    this._actionLabel = actionLabel;
    this._onAction = onAction;
    this._pauseOnInteraction = pauseOnInteraction;
    this._onClose = onClose;
    // Relative position container
    this._container = container;

    this._element = null;
    this._timeoutId = null;
    this.isDismissed = false;
  }

  show() {
    const container = this._container ?? getContainer();
    container.classList.add('toast-container');

    this._element = document.createElement('div');
    this._element.className = `toast toast--${this._type}`;
    this._element.setAttribute('role', this._type === ToastType.ERROR ? 'alert' : 'status');
    this._element.setAttribute(
      'aria-live',
      this._type === ToastType.ERROR ? 'assertive' : 'polite',
    );

    const iconEl = this._iconKey ? createIcon(this._iconKey, 'toast__icon') : null;
    const contentEl = document.createElement('div');
    contentEl.className = 'toast__content';

    const messageEl = document.createElement('p');
    messageEl.className = 'toast__message';
    messageEl.textContent = this._message;
    contentEl.appendChild(messageEl);

    const actionButton = this._actionLabel ? document.createElement('button') : null;
    if (actionButton) {
      actionButton.type = 'button';
      actionButton.className = 'toast__action';
      actionButton.textContent = this._actionLabel;
      actionButton.addEventListener('click', () => {
        if (this.isDismissed) return;

        try {
          this._onAction?.();
        } finally {
          this.dismiss();
        }
      });
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'toast__close';
    closeButton.setAttribute('aria-label', this._closeLabel);
    closeButton.addEventListener('click', () => this.dismiss());

    const closeIconEl = document.createElement('i');
    closeIconEl.className = 'eva eva-close-outline';
    closeIconEl.setAttribute('aria-hidden', 'true');
    closeButton.appendChild(closeIconEl);

    if (iconEl) {
      this._element.appendChild(iconEl);
    }
    this._element.appendChild(contentEl);
    if (actionButton) this._element.appendChild(actionButton);
    this._element.appendChild(closeButton);

    if (this._autoDismiss > 0 && this._pauseOnInteraction) {
      this._element.addEventListener('mouseenter', () => this._clearAutoDismiss());
      this._element.addEventListener('mouseleave', () => this._resetAutoDismiss());
      this._element.addEventListener('focusin', () => this._clearAutoDismiss());
      this._element.addEventListener('focusout', () => this._resetAutoDismiss());
    }

    container.appendChild(this._element);

    if (prefersReducedMotion()) {
      this._element.classList.add('toast--visible');
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._element.classList.add('toast--visible'));
      });
    }

    this._resetAutoDismiss();

    return this;
  }

  updateMessage(message) {
    this._message = message;

    if (this._element) {
      const messageEl = this._element.querySelector('.toast__message');
      if (messageEl) messageEl.textContent = message;
    }

    return this;
  }

  dismiss() {
    if (this.isDismissed || !this._element) return;

    this.isDismissed = true;
    this._clearAutoDismiss();

    const element = this._element;

    const removeElement = () => {
      element.remove();
      if (typeof this._onClose === 'function') this._onClose();
    };

    if (prefersReducedMotion()) {
      removeElement();
    } else {
      element.classList.remove('toast--visible');
      element.addEventListener('transitionend', removeElement, { once: true });
    }
  }

  _clearAutoDismiss() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }

  _resetAutoDismiss() {
    if (this._autoDismiss <= 0 || this.isDismissed) return;

    this._timeoutId = setTimeout(() => this.dismiss(), this._autoDismiss);
  }
}

export function showToast(options) {
  return new Toast(options).show();
}
